import { useState, useMemo, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Stack,
  IconButton,
  Collapse,
  Checkbox,
  Snackbar,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../../auth";
import { useQuery } from "@tanstack/react-query";
import {
  getListPermissionQueryOptions,
  getListAccessProfilesQueryOptions,
  getListStockingAreaQueryOptions,
  getListUserQueryOptions,
  getFindGroupQueryOptions,
  getEditGroupMutationOptions,
} from "../../../../../api/bpm/endpoints";

import { SmartPermissionDtoApi, SmartGroupWitlAllPropertiesDto } from "../../../../../api/bpm/model";
import { useTranslation } from "react-i18next";
import { deduplicatePermissions } from "../../../../../utils/permissionTreeMapper";

export const Route = createFileRoute("/__auth/admin/groups/$groupId/edit")({
  component: GroupEditPage,
  staticData: {
    title: "routes.editGroup",
  },
});

const schema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  description: z.string().min(1, "Descrição é obrigatória"),
  enabled: z.boolean(),
  permissions: z.array(z.number()),
  accessProfiles: z.array(z.object({ id: z.number(), label: z.string() }).passthrough()),
  stockingAreas: z.array(z.object({ id: z.number(), label: z.string() }).passthrough()),
  users: z.array(z.object({ id: z.string(), label: z.string() }).passthrough()),
});

type FormValues = z.infer<typeof schema>;

interface PermissionLeaf {
  id: number;
  label: string;
  checked: boolean;
}

interface ModuleNode {
  name: string;
  permissions: PermissionLeaf[];
  allChecked: boolean;
  someChecked: boolean;
}

function buildPermissionTree(
  allPermissions: SmartPermissionDtoApi[],
  selectedPermissionIds: number[],
): ModuleNode[] {
  const moduleMap = new Map<string, PermissionLeaf[]>();
  const collator = new Intl.Collator("pt-BR", {
    sensitivity: "base",
    numeric: true,
  });

  for (const p of deduplicatePermissions(allPermissions, selectedPermissionIds)) {
    if (p.id == null) continue;

    const moduleName = p.module || "Outros";

    if (!moduleMap.has(moduleName)) {
      moduleMap.set(moduleName, []);
    }
    const isChecked = selectedPermissionIds.includes(p.id);
    moduleMap.get(moduleName)!.push({
      id: p.id,
      label: p.functionality || p.permission || `Permissão ${p.id}`,
      checked: isChecked,
    });
  }

  const tree: ModuleNode[] = [];
  const orderedModules = Array.from(moduleMap.keys()).sort((a, b) => {
    if (a === "Outros") return 1;
    if (b === "Outros") return -1;
    return collator.compare(a, b);
  });

  for (const moduleName of orderedModules) {
    const perms = moduleMap.get(moduleName)!;
    const orderedPermissions = [...perms].sort(
      (a, b) => collator.compare(a.label, b.label) || a.id - b.id,
    );
    const checkedCount = perms.filter((p) => p.checked).length;
    tree.push({
      name: moduleName,
      permissions: orderedPermissions,
      allChecked: perms.length > 0 && checkedCount === perms.length,
      someChecked: checkedCount > 0 && checkedCount < perms.length,
    });
  }

  return tree;
}

function PermissionTreeModule({
  module,
  onToggleModule,
  onTogglePermission,
}: {
  module: ModuleNode;
  onToggleModule: (moduleName: string, checked: boolean) => void;
  onTogglePermission: (permId: number, checked: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.5,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover", borderRadius: 1 },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ mr: 0.5 }}>
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Checkbox
          checked={module.allChecked}
          indeterminate={module.someChecked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggleModule(module.name, e.target.checked)}
          size="small"
          sx={{ p: 0.5 }}
        />
        <Typography variant="body2" sx={{ ml: 1 }}>
          {module.name}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 6 }}>
          {module.permissions.map((perm) => (
            <Box
              key={perm.id}
              sx={{
                display: "flex",
                alignItems: "center",
                py: 0.25,
              }}
            >
              <Checkbox
                checked={perm.checked}
                onChange={(e) => onTogglePermission(perm.id, e.target.checked)}
                size="small"
                sx={{
                  p: 0.5,
                  "&.Mui-checked": { color: "primary.main" },
                }}
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {perm.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function GroupEditPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { groupId } = Route.useParams();
  const tenant = user?.tenant || "default-domain";
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

  const {
    data: groupData,
    isLoading: isLoadingGroup,
    error: errorGroup,
  } = useQuery({
    ...getFindGroupQueryOptions(tenant, groupId),
  });
  const group = ((groupData as any)?.data || groupData) as SmartGroupWitlAllPropertiesDto | undefined;

  const { data: allPermissionsData, isLoading: isLoadingAllPermissions } =
    useQuery({
      ...getListPermissionQueryOptions({
        isPageable: false,
        locale: "pt",
      } as any),
    });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      enabled: true,
      permissions: [],
      accessProfiles: [],
      stockingAreas: [],
      users: [],
    },
  });

  const { data: accessProfilesData, isLoading: isLoadingAccessProfiles } =
    useQuery({
      ...getListAccessProfilesQueryOptions(tenant, {
          isPageable: false
    } as any),
    });

  const { data: stockingAreasData, isLoading: isLoadingStockingAreas } =
    useQuery({
      ...getListStockingAreaQueryOptions(tenant, {
          isPageable: false
    } as any),
    });

  const { data: usersData, isLoading: isLoadingUsers } =
    useQuery({
      ...getListUserQueryOptions(tenant, {
          isPageable: false,
          listOrganizationMembers: true,
    } as any),
    });

  const isLoading =
    isLoadingGroup ||
    isLoadingAllPermissions ||
    isLoadingAccessProfiles ||
    isLoadingStockingAreas ||
    isLoadingUsers;

  useEffect(() => {
    if (group) {
      reset({
        name: group.name || "",
        description: group.description || "",
        enabled: group.enabled ?? true,
        permissions: (group.permissions ?? [])
          .map((p) => p.id)
          .filter((id): id is number => id !== undefined),
        accessProfiles:
          group.accessProfiles?.map((ap) => ({
            id: ap.id ?? 0,
            label: ap.name ?? "",
          })) ?? [],
        stockingAreas:
          group.stockingAreas?.map((sa) => ({
            id: sa.id ?? 0,
            label: sa.name ?? "",
          })) ?? [],
        users:
          group.users?.map((u) => {
            const namePart = u.name || "Sem nome";
            const emailPart = u.email ? ` - ${u.email}` : "";
            const groupTenant = group?.tenant || tenant;
            const userTenant = (u as any).tenant ? String((u as any).tenant) : undefined;
            const isDifferentTenant = Boolean(userTenant && groupTenant && userTenant !== groupTenant);
            return {
              id: u.id ?? "",
              label: `${namePart}${emailPart}`,
              name: namePart,
              email: u.email,
              tenant: userTenant,
              isDifferentTenant,
            };
          }) ?? [],
      });
    }
  }, [group, reset]);

  const mutation = useMutation({
    ...getEditGroupMutationOptions(),
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: t("admin.groups.updateSuccess"),
        severity: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["getGroup"],
      });
      queryClient.invalidateQueries({
        queryKey: ["listGroupPermissions"],
      });
      navigate({ to: `/admin/groups/${groupId}` });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: t("admin.groups.updateError"),
        severity: "error",
      });
      console.error(error);
    },
  });

  const onSubmit = (data: FormValues) => {
    mutation.mutate({
      groupId,
      tenant,
      data: {
        name: data.name,
        description: data.description,
        permissions: data.permissions.map((id) => ({ id })),
        stockingAreas: data.stockingAreas.map((sa) => ({ id: sa.id })),
        accessProfiles: data.accessProfiles.map((ap) => ({ id: ap.id })),
        users: data.users.map((u) => ({ id: u.id })),
      },
    });
  };

  const selectedPermissionIds = watch("permissions");

  const permissionTree = useMemo(() => {
    let allPerms: SmartPermissionDtoApi[] = [];
    const responseData = (allPermissionsData as any)?.data || allPermissionsData;
    if (Array.isArray(responseData)) {
      allPerms = responseData;
    } else if (responseData && typeof responseData === "object") {
      // @ts-ignore - API type inconsistency handling
      const page = responseData as any;
      if (Array.isArray(page.content)) {
        allPerms = page.content;
      }
    }

    return buildPermissionTree(allPerms, selectedPermissionIds || []);
  }, [allPermissionsData, selectedPermissionIds]);

  const handleToggleModule = (moduleName: string, checked: boolean) => {
    const moduleNode = permissionTree.find((m) => m.name === moduleName);
    if (!moduleNode) return;

    const modulePermIds = moduleNode.permissions.map((p) => p.id);
    const currentIds = new Set(selectedPermissionIds);

    if (checked) {
      modulePermIds.forEach((id) => currentIds.add(id));
    } else {
      modulePermIds.forEach((id) => currentIds.delete(id));
    }

    setValue("permissions", Array.from(currentIds), { shouldDirty: true });
  };

  const handleTogglePermission = (permId: number, checked: boolean) => {
    const currentIds = new Set(selectedPermissionIds);
    if (checked) {
      currentIds.add(permId);
    } else {
      currentIds.delete(permId);
    }
    setValue("permissions", Array.from(currentIds), { shouldDirty: true });
  };

  const allModulesChecked =
    permissionTree.length > 0 && permissionTree.every((m) => m.allChecked);
  const someModulesChecked =
    permissionTree.some((m) => m.allChecked || m.someChecked) &&
    !allModulesChecked;

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      const allIds = permissionTree.flatMap((m) =>
        m.permissions.map((p) => p.id),
      );
      setValue("permissions", allIds, { shouldDirty: true });
    } else {
      setValue("permissions", [], { shouldDirty: true });
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (errorGroup) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Erro ao carregar dados do grupo.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate({ to: `/admin/groups`, search: { page: 1, size: 10 } })
          }
        >{t("common.back")}</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>{t("admin.groups.editTitle")} {group?.name}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Dados Básicos
        </Typography>

        <Stack spacing={2.5} sx={{ mb: 4 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome do Grupo"
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.common.description")}
                fullWidth
                size="small"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />

          <Controller
            name="accessProfiles"
            control={control}
            render={({
              field: { onChange, value, ref, ...field },
              fieldState: { error },
            }) => (
              <Autocomplete
                {...field}
                multiple
                options={
                  ((accessProfilesData as any)?.content || (accessProfilesData as any)?.data?.content || []).map((ap: any) => ({
                    id: ap.id,
                    label: ap.name,
                  }))
                }
                value={value}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, data) => onChange(data)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={ref}
                    label="Perfis de Acesso"
                    error={!!error}
                    helperText={error?.message}
                    size="small"
                  />
                )}
              />
            )}
          />

          <Controller
            name="users"
            control={control}
            render={({
              field: { onChange, value, ref, ...field },
              fieldState: { error },
            }) => (
              <Autocomplete
                {...field}
                multiple
                options={
                  ((usersData as any)?.content || (usersData as any)?.data?.content || []).map((u: any) => {
                    const namePart = u.name || "Sem nome";
                    const emailPart = u.email ? ` - ${u.email}` : "";
                    const groupTenant = group?.tenant || tenant;
                    const userTenant = u.tenant ? String(u.tenant) : undefined;
                    const isDifferentTenant = Boolean(userTenant && groupTenant && userTenant !== groupTenant);
                    return {
                      id: u.id,
                      label: `${namePart}${emailPart}`,
                      name: namePart,
                      email: u.email,
                      tenant: userTenant,
                      isDifferentTenant,
                    };
                  })
                }
                value={value}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                onChange={(_, data) => onChange(data)}
                renderOption={(props, option: any) => {
                  const { key, ...optionProps } = props as any;
                  return (
                    <Box component="li" key={key || option.id} {...optionProps}>
                      <Typography variant="body2">
                        {option.name}
                        {option.email ? ` - ${option.email}` : ""}
                        {Boolean(option.isDifferentTenant && option.tenant) && (
                          <Box
                            component="span"
                            sx={{
                              color: "warning.dark",
                              fontWeight: 600,
                              ml: 1,
                            }}
                          >
                            ({option.tenant})
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  );
                }}
                renderTags={(tagValue: any[], getTagProps) =>
                  tagValue.map((option: any, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key || option.id}
                        {...tagProps}
                        size="small"
                        label={
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <span>{option.name}{option.email ? ` - ${option.email}` : ""}</span>
                            {Boolean(option.isDifferentTenant && option.tenant) && (
                              <Box
                                component="span"
                                sx={{
                                  color: "warning.dark",
                                  fontWeight: 600,
                                }}
                              >
                                ({option.tenant})
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={ref}
                    label="Usuários"
                    error={!!error}
                    helperText={error?.message}
                    size="small"
                  />
                )}
              />
            )}
          />

          <Controller
            name="stockingAreas"
            control={control}
            render={({
              field: { onChange, value, ref, ...field },
              fieldState: { error },
            }) => (
              <Autocomplete
                {...field}
                multiple
                options={
                  ((stockingAreasData as any)?.content || (stockingAreasData as any)?.data?.content || []).map((sa: any) => ({
                    id: sa.id,
                    label: sa.name,
                  }))
                }
                value={value}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, data) => onChange(data)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={ref}
                    label="Áreas de Lotação"
                    error={!!error}
                    helperText={error?.message}
                    size="small"
                  />
                )}
              />
            )}
          />
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Permissões de Acesso
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Checkbox
              checked={allModulesChecked}
              indeterminate={someModulesChecked}
              onChange={(e) => handleToggleAll(e.target.checked)}
              size="small"
              sx={{ p: 0.5 }}
            />
            <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
              Todos os módulos
            </Typography>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {permissionTree.length > 0 ? (
            <Box sx={{ pl: 2 }}>
              {permissionTree.map((module) => (
                <PermissionTreeModule
                  key={module.name}
                  module={module}
                  onToggleModule={handleToggleModule}
                  onTogglePermission={handleTogglePermission}
                />
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
              Nenhuma permissão disponível.
            </Typography>
          )}
        </Paper>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() =>
              navigate({ to: "/admin/groups", search: { page: 1, size: 10 } })
            }
            disabled={mutation.isPending}
          >{t("common.cancel").toUpperCase()}</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={mutation.isPending}
          >
            SALVAR ALTERAÇÕES
          </Button>
        </Box>
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
